import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * OAuth Callback Handler for Emergent Google Auth
 * Processes session_id from URL fragment and exchanges it for session token
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithOAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        console.log('AuthCallback: Processing hash:', hash);
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('AuthCallback: No session_id found in URL');
          navigate('/login', { replace: true, state: { error: 'No session ID received from Google' } });
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('AuthCallback: Got session_id, exchanging for token...');
        
        // Exchange session_id for user data via backend
        const response = await axios.post(`${API}/auth/google/callback`, {
          session_id: sessionId
        });

        const { token, user } = response.data;
        console.log('AuthCallback: Got token and user, storing auth...');

        // Use the proper auth context method to store credentials
        loginWithOAuth(token, user);

        console.log('AuthCallback: Auth stored, redirecting to:', user.role === 'admin' ? '/admin' : '/portal');
        
        // Small delay to ensure state is updated
        setTimeout(() => {
          // Navigate to appropriate dashboard
          if (user.role === 'admin') {
            window.location.href = '/admin';
          } else {
            window.location.href = '/portal';
          }
        }, 100);

      } catch (error) {
        console.error('AuthCallback: Auth failed:', error.response?.data || error.message);
        navigate('/login', { 
          replace: true,
          state: { error: error.response?.data?.detail || 'Authentication failed. Please try again.' }
        });
      }
    };

    processAuth();
  }, [location, navigate, loginWithOAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <Loader size={40} className="animate-spin text-[#0B1F3A] mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-2">Completing sign in...</h2>
        <p className="text-[#475569]">Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
